import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import ShortcutIcon from '../../../views/common/icons/ShortcutIcon';
import styles from './shortcuts.module.scss';
import { getPinnedShortcuts } from "@/utils/shortcutsUtils.ts";
import type { Shortcut } from './type';

import { useTranslation } from 'react-i18next';
import showUserMessage from '../../../utils/showUserMessage.ts';
import Popper from '@mui/material/Popper';
import PinnedShortcutTile from './PinnedShortcutTile.tsx';
import Tooltip from '@mui/material/Tooltip';
import { GearIcon } from '@phosphor-icons/react/dist/csr/Gear';
import cn from 'classnames';

type ShortcutProps = {
  onOpen?: () => void;
};

const PinnedShortcuts: FC<ShortcutProps> = ({ onOpen }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'shortcuts' });
  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [shortcutList, setShortcutList] = useState<Shortcut[]>(getPinnedShortcuts());
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const onPinnedShortcutsChanges = (e: StorageEvent) => {
    if (e.key === 'shortcuts') {
      setTimeout(() => {
        setShortcutList(getPinnedShortcuts());
      }, 100);
    }
  };

  const onDropdownClose = (e: Event) => {
    const path = e.composedPath();

    if (!dropdownRef.current || !anchorRef.current) {
      return;
    }

    if (path.includes(dropdownRef.current) || path.includes(anchorRef.current)) {
      return;
    }

    setOpen(false);
    setAnchorEl(null);
  };

  useEffect(() => {
    const wrongTranslation = getPinnedShortcuts()?.some(sc => !sc.titles || !Object.keys(sc.titles).length);
    if (wrongTranslation) {
      showUserMessage('Some shortcuts may have wrong naming. Please remove them and pin again', 'info');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('storage', onPinnedShortcutsChanges);
    document.addEventListener('click', onDropdownClose);

    return () => {
      window.removeEventListener('storage', onPinnedShortcutsChanges);
      document.removeEventListener('click', onDropdownClose);
    };
  }, []);

  const onDropdownToggle = () => {
    if (open) {
      setOpen(false);
      setAnchorEl(null);
    } else {
      onDropdownOpen();
    }
  };

  const onDropdownOpen = () => {
    setOpen(true);
    setAnchorEl(anchorRef.current);
    setShortcutList(getPinnedShortcuts());
    onOpen?.();
  };

  const onShortcutNavigate = (sc: Shortcut) => {
    window.location.replace(`/${sc.module}${sc.path}`);
  };

  const onPreferencesNavigate = () => {
    window.location.replace('/users/preferences#shortcuts');
  };

  return (
    <div
      data-testid="shortcut-dropdown"
      className={cn(styles.shortcutWrapper, { [styles.shortcutWrapperActive]: open })}
    >
      <div data-testid="shortcut-button" ref={anchorRef} onClick={onDropdownToggle} className={styles.shortcutIcon}>
        <ShortcutIcon />
      </div>
      <Popper open={open} anchorEl={anchorEl} disablePortal={true}>
        <div ref={dropdownRef} className={styles.shortcutContainer}>
          <div className={styles.shortcutHeader}>
            <span>{t('title')}</span>
            <Tooltip placement="bottom-end" classes={{ popper: styles.tooltip }} title={t('preferences')}>
              <span className={styles.preferencesIcon} onClick={onPreferencesNavigate}>
                <GearIcon size={16} />
              </span>
            </Tooltip>
          </div>
          <div className={styles.shortcutList}>
            {shortcutList.length ? (
              shortcutList.map(shortcut => {
                return (
                  <PinnedShortcutTile key={shortcut.id} shortcut={shortcut} onShortcutNavigate={onShortcutNavigate} />
                );
              })
            ) : (
              <div className={styles.noShortcut}>{t('noShortcuts')}</div>
            )}
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default PinnedShortcuts;
